import { Comment } from "@mui/icons-material";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  EMPTY_TIPTAP_COMMENT_THREADS,
  EMPTY_TIPTAP_SUGGESTIONS,
  getSelectedTiptapCollaborationItemKey,
  getTiptapCollaborationItemKey,
  getTiptapCollaborationItems,
  getVisibleTiptapCollaborationThreads,
  isTiptapThreadUnresolved,
  type TiptapCollabPanelFilter,
} from "./tiptapCollabPanelItems";
import {
  getTiptapSuggestionThreadBySuggestionId,
  getVisibleTiptapThreadComments,
} from "./tiptapCommentUtils";
import { TiptapSuggestionCard } from "./TiptapSuggestionCard";
import {
  TiptapCollaborationCardFrame,
  TiptapThreadDiscussion,
} from "./TiptapThreadDiscussion";
import type {
  TiptapCommentThread,
  TiptapCommentsController,
  TiptapSuggestionsController,
} from "./types";

type TiptapCollabPanelProps = {
  controller?: TiptapCommentsController | null;
  getThreadLinkUrl?: (threadId: string) => string;
  onThreadAnchorUnavailable?: () => void;
  suggestionsController?: TiptapSuggestionsController | null;
};

type TiptapCommentThreadCardProps = {
  controller: TiptapCommentsController;
  getThreadLinkUrl?: (threadId: string) => string;
  highlighted: number;
  thread: TiptapCommentThread;
};

const isInteractiveTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return Boolean(
    target.closest('button,input,textarea,select,a,[role="menuitem"]')
  );
};

const TiptapCommentThreadCard = ({
  controller,
  getThreadLinkUrl,
  highlighted,
  thread,
}: TiptapCommentThreadCardProps) => {
  const visibleComments = getVisibleTiptapThreadComments(thread);
  const unresolved = isTiptapThreadUnresolved(thread);

  return (
    <TiptapCollaborationCardFrame
      borderColor={unresolved ? "rgb(229, 231, 235)" : "rgb(34, 197, 94)"}
      highlighted={highlighted}
      highlightAnimationPrefix="tiptapCommentHighlightFade"
    >
      <TiptapThreadDiscussion
        controller={controller}
        getThreadLinkUrl={getThreadLinkUrl}
        thread={thread}
        variant="document"
        visibleComments={visibleComments}
      />
    </TiptapCollaborationCardFrame>
  );
};

export const TiptapCollabPanel = ({
  controller,
  getThreadLinkUrl,
  onThreadAnchorUnavailable,
  suggestionsController,
}: TiptapCollabPanelProps) => {
  const [filter, setFilter] = useState<TiptapCollabPanelFilter>("all");
  const [documentPositionVersion, setDocumentPositionVersion] = useState(0);
  const [clickedItem, setClickedItem] = useState<{
    itemKey: string | null;
    token: number;
  }>({ itemKey: null, token: 0 });
  const itemRefs = useRef<Map<string, HTMLElement> | null>(null);

  if (!itemRefs.current) {
    itemRefs.current = new Map();
  }

  const threads = controller?.threads ?? EMPTY_TIPTAP_COMMENT_THREADS;
  const suggestionThreads =
    controller?.suggestionThreads ?? EMPTY_TIPTAP_COMMENT_THREADS;
  const suggestions =
    suggestionsController?.suggestions ?? EMPTY_TIPTAP_SUGGESTIONS;
  const collaborationEditor =
    controller?.editor ?? suggestionsController?.editor ?? null;
  const allKnownThreads = useMemo(
    () => [...threads, ...suggestionThreads],
    [threads, suggestionThreads]
  );
  const visibleThreads = useMemo(
    () => getVisibleTiptapCollaborationThreads(threads),
    [threads]
  );
  const suggestionThreadsBySuggestionId = useMemo(
    () => getTiptapSuggestionThreadBySuggestionId(suggestionThreads),
    [suggestionThreads]
  );
  const setItemRef = useCallback(
    (itemKey: string) => (element: HTMLElement | null) => {
      if (element) {
        itemRefs.current?.set(itemKey, element);
      } else {
        itemRefs.current?.delete(itemKey);
      }
    },
    []
  );
  const selectedItemKey = useMemo(
    () =>
      getSelectedTiptapCollaborationItemKey({
        selectedThreadId: controller?.selectedThreadId ?? null,
        suggestions,
        threads: allKnownThreads,
        visibleThreads,
      }) ?? clickedItem.itemKey,
    [
      allKnownThreads,
      clickedItem.itemKey,
      controller?.selectedThreadId,
      suggestions,
      visibleThreads,
    ]
  );
  const collaborationItems = useMemo(
    () =>
      getTiptapCollaborationItems({
        editor: collaborationEditor,
        filter,
        suggestions,
        threads: visibleThreads,
      }),
    [
      collaborationEditor,
      documentPositionVersion,
      filter,
      suggestions,
      visibleThreads,
    ]
  );
  const isSelectedItemVisible = Boolean(
    selectedItemKey &&
      collaborationItems.some(
        (item) =>
          getTiptapCollaborationItemKey(item.kind, item.id) === selectedItemKey
      )
  );
  const unresolvedCount = visibleThreads.filter(
    isTiptapThreadUnresolved
  ).length;
  const resolvedCount = visibleThreads.length - unresolvedCount;
  const openCount = unresolvedCount + suggestions.length;
  const allCount = visibleThreads.length + suggestions.length;

  const handleSelectThread = (threadId: string) => {
    const itemKey = getTiptapCollaborationItemKey("comment", threadId);
    setClickedItem(({ token }) => ({ itemKey, token: token + 1 }));
    const selectedInDocument = controller?.selectThread(threadId) ?? false;

    if (!selectedInDocument) {
      onThreadAnchorUnavailable?.();
    }
  };
  const handleSelectSuggestion = (suggestionId: string) => {
    const itemKey = getTiptapCollaborationItemKey("suggestion", suggestionId);
    setClickedItem(({ token }) => ({ itemKey, token: token + 1 }));
    const editor = suggestionsController?.editor ?? controller?.editor ?? null;

    const targets = Array.from(
      editor?.view.dom.querySelectorAll<HTMLElement>(
        "[data-suggestion-id], [data-suggestion]"
      ) ?? []
    ).filter(
      (target) =>
        target.getAttribute("data-suggestion-id") === suggestionId ||
        target.getAttribute("data-suggestion") === suggestionId
    );

    targets[0]?.scrollIntoView({ behavior: "smooth", block: "center" });
    targets.forEach((target) =>
      target.animate([{ opacity: 0.15 }, { opacity: 1 }], {
        delay: 300,
        duration: 2000,
        easing: "ease-out",
      })
    );
  };

  useEffect(() => {
    if (!collaborationEditor) {
      return;
    }

    const refreshDocumentPositions = () => {
      setDocumentPositionVersion((version) => version + 1);
    };

    collaborationEditor.on("transaction", refreshDocumentPositions);
    return () => {
      collaborationEditor.off("transaction", refreshDocumentPositions);
    };
  }, [collaborationEditor]);

  useLayoutEffect(() => {
    if (!selectedItemKey || !isSelectedItemVisible) {
      return;
    }

    itemRefs.current
      ?.get(selectedItemKey)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [isSelectedItemVisible, selectedItemKey]);

  return (
    <Box className="flex h-full flex-col" sx={{ minHeight: 0 }}>
      <Box className="border-b border-gray-200 px-3 py-2">
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_event, nextFilter: TiptapCollabPanelFilter | null) => {
            if (nextFilter) {
              setFilter(nextFilter);
            }
          }}
          size="small"
          fullWidth
        >
          <ToggleButton value="all" sx={{ textTransform: "none", flex: 1 }}>
            All ({allCount})
          </ToggleButton>
          <ToggleButton
            value="unresolved"
            sx={{ textTransform: "none", flex: 1 }}
          >
            Open ({openCount})
          </ToggleButton>
          <ToggleButton
            value="resolved"
            sx={{ textTransform: "none", flex: 1 }}
          >
            Resolved ({resolvedCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box className="flex-1 overflow-auto px-3 py-2">
        {collaborationItems.length === 0 ? (
          <Box className="py-8 text-center text-gray-500">
            <Comment
              sx={{ fontSize: 48, color: "gray.300", marginBottom: 2 }}
            />
            <Typography variant="body2">
              {filter === "all"
                ? "No activity yet"
                : filter === "unresolved"
                  ? "No open comments or suggestions"
                  : "No resolved comments"}
            </Typography>
            {filter === "all" && (
              <Typography variant="caption" className="text-gray-400">
                Select text in the document to add a comment
              </Typography>
            )}
          </Box>
        ) : (
          <Box className="space-y-4">
            {collaborationItems.map((item) => {
              const itemKey = getTiptapCollaborationItemKey(item.kind, item.id);

              if (item.kind === "suggestion") {
                return suggestionsController ? (
                  <Box
                    key={itemKey}
                    ref={setItemRef(itemKey)}
                    onClick={(event) => {
                      if (!isInteractiveTarget(event.target)) {
                        handleSelectSuggestion(item.suggestion.id);
                      }
                    }}
                  >
                    <TiptapSuggestionCard
                      commentsController={controller}
                      controller={suggestionsController}
                      highlighted={
                        selectedItemKey === itemKey ? clickedItem.token || 1 : 0
                      }
                      linkedThread={suggestionThreadsBySuggestionId.get(
                        item.suggestion.id
                      )}
                      orgUsers={controller?.orgUsers ?? []}
                      suggestion={item.suggestion}
                    />
                  </Box>
                ) : null;
              }

              if (!controller) {
                return null;
              }

              return (
                <Box
                  key={itemKey}
                  ref={setItemRef(itemKey)}
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    if (!isInteractiveTarget(event.target)) {
                      handleSelectThread(item.thread.id);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (isInteractiveTarget(event.target)) {
                      return;
                    }

                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleSelectThread(item.thread.id);
                    }
                  }}
                  onMouseEnter={() => controller.hoverThread(item.thread.id)}
                  onMouseLeave={() => controller.unhoverThread(item.thread.id)}
                  sx={{ outline: 0 }}
                >
                  <TiptapCommentThreadCard
                    controller={controller}
                    getThreadLinkUrl={getThreadLinkUrl}
                    highlighted={
                      selectedItemKey === itemKey ? clickedItem.token || 1 : 0
                    }
                    thread={item.thread}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};
